import React, { forwardRef } from 'react';
import styles from './ProfileSidebar.module.scss';
import Typography from '~/shared/components/Typography/Typography';
import { ProfileTabType } from './ProfileSidebar';

type BadgePillProps = {
    value: number;
    unit: string;
};

type TabItemProps = {
    id: ProfileTabType | 'logout';
    label: string;
    icon: React.ReactNode;
    badge?: {
        value: number;
        unit: string;
    };
    isActive?: boolean;
    onClick: () => void;
    tabIndex?: number;
    onKeyDown?: (e: React.KeyboardEvent) => void;
};

const BadgePill: React.FC<BadgePillProps> = ({ value, unit }) => {
    return (
        <span className={styles.badge}>
            <i>•</i>
            {`${value} ${unit}`}
        </span>
    );
};

const TabItem = forwardRef<HTMLButtonElement, TabItemProps>(
    ({ id, label, icon, badge, isActive = false, onClick, tabIndex = 0, onKeyDown }, ref) => {
        const isTabButton = id !== 'logout';

        return (
            <button
                ref={ref}
                className={`${styles.tabItem} ${isActive ? styles.active : ''}`}
                onClick={onClick}
                onKeyDown={onKeyDown}
                aria-selected={isTabButton ? isActive : undefined}
                role={isTabButton ? 'tab' : undefined}
                aria-label={`${label}${badge && badge.value > 0 ? ` (${badge.value} ${badge.unit})` : ''}`}
                tabIndex={tabIndex}
            >
                <div className={styles.iconWrapper} aria-hidden="true">
                    {icon}
                </div>
                <Typography
                    className={styles.tabLabel}
                    variant="paragraph-M"
                    as="span"
                    weight={isActive ? 'medium' : 'regular'}
                >
                    {label}
                    {badge && badge.value > 0 && (
                        <BadgePill value={badge.value} unit={badge.unit} />
                    )}
                </Typography>
            </button>
        );
    },
);

TabItem.displayName = 'TabItem';

export default TabItem;
